import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContext } from 'src/context/PageContext';
import { FeedItem } from 'types/feeds';
import { ContentMixedListMetadata, ContentType, SectionType } from 'types/publishedAssets';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { useScreenSize } from 'utils/useScreenSize';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { BlockHeading } from 'components/Content/Atoms/BlockHeading';
import { getMixedListSortParams, MIXED_LIST_DEFAULT_PAGE_SIZE } from 'src/serverUtils/helpers';
import { FeedItemCard } from '../Feed/FeedItemCard';
import { useParticipationCounts } from '../Feed/hooks';
import { useMixedList } from './hooks/useMixedList';
import styles from './MixedList.module.scss';

interface MixedListProps {
  content: ContentType;
  section: SectionType;
}

/**
 * A manually curated, cross-type feed panel. The selection lives server-side keyed by this
 * block's content id; every item renders with the existing type-agnostic FeedItemCard, receiving
 * its universe per item (item.itemType) instead of per block.
 */
export function MixedList(props: MixedListProps) {
  const { content, section } = props;
  const { t } = useTranslation();
  const { isMobileView } = useScreenSize();
  const { numberOfColumns } = section.metadata || {};
  const { display_type: displayType } = content;
  const metadata = content.metadata as ContentMixedListMetadata;
  const { feeds, page, urlParams, manifest, preview } = useContext(PageContext);
  const { translate } = useGetContentTranslations();

  const initialFeed = feeds?.[content.id];
  const pageSize = metadata.page_size ?? MIXED_LIST_DEFAULT_PAGE_SIZE;

  const { feed, hasMore, isLoadingMore, loadMore } = useMixedList(
    initialFeed,
    { kind: 'page', id: page?.id || '' },
    {
      locale: urlParams?.locale || '',
      organization: urlParams?.organization,
      contentId: content.id,
      // Preview must reflect the live draft selection, not the last-published frozen snapshot.
      version: preview ? undefined : manifest?.version,
      ...getMixedListSortParams(metadata),
      pageSize,
      preview,
    },
  );

  const activityItems = useMemo<FeedItem[]>(
    () => feed.filter((item) => item.itemType === 'engagement_activity'),
    [feed],
  );
  const { participationCounts } = useParticipationCounts({ feed: activityItems, feedType: 'engagement_activity' });

  const getParticipationCount = (item: FeedItem): number | undefined => {
    switch (item.type) {
      case 'Engagement':
        return participationCounts.engagements?.[item.id];
      case 'DiscussionTopic':
        return participationCounts.discussionTopics?.[item.id];
      case 'IdeaBoard':
        return participationCounts.ideaBoards?.[item.id];
      default:
        return undefined;
    }
  };

  const getDisplayClass = () => {
    if (isMobileView || numberOfColumns === 2) return styles.feedList;
    return displayType === 'feed_list' ? styles.feedList : styles.feedGrid;
  };

  const getCardVariant = () => {
    if (isMobileView) return 'imageTop';
    return displayType === 'feed_list' && numberOfColumns !== 2 ? 'imageLeft' : 'imageTop';
  };

  if (feed.length === 0) return null;

  const label = translate(content.translations, 'label');
  const secondaryText = translate(content.translations, 'description');

  return (
    <div className={styles.feedContainer}>
      <BlockHeading title={label} secondaryText={secondaryText} />
      <div className={[styles.feedItemsContainer, getDisplayClass()].join(' ')}>
        {feed.map((item) => (
          <FeedItemCard
            key={`${item.itemType}-${item.id}`}
            data={item}
            type={item.itemType || 'project'}
            variant={getCardVariant()}
            isTwoColumns={numberOfColumns === 2}
            participationCount={getParticipationCount(item)}
            content={content}
            isMixedList
          />
        ))}
      </div>
      {hasMore && (
        <ActionButton
          className={styles.loadMoreButton}
          type={ButtonType.ACTION_SECONDARY}
          onClick={() => {
            loadMore().catch(console.error);
          }}
          disabled={isLoadingMore}
        >
          {t('mixedList.loadMore', 'Load more')}
        </ActionButton>
      )}
    </div>
  );
}
